/**
 * useAllPageAccess Hook
 *
 * Checks which pages the current user has access to across the entire application.
 * Used by the index page and sidebar to gray out links for pages the user cannot access.
 *
 * **Performance Optimized:**
 * - Reads from Zustand store (no Firestore listener)
 * - Falls back to localStorage cache for cross-app navigation
 * - Eliminates redundant Firestore reads
 *
 * Usage:
 *   const { accessiblePages, isLoading } = useAllPageAccess(userEmail);
 *   const hasAccess = accessiblePages.has('rentals');
 */

import { useState, useEffect, useMemo } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { PAGES } from '../config/pages.config';
import type { UserPermissions, PageAccessConfig } from '../types/permissions.types';
import { grantedLocationIds, holdsManagerFlag, isLocationBasedAccess } from '../types/permissions.types';

// Use same cache as usePagePermissions
const PERMISSION_CACHE_KEY = 'user_permissions_cache';
const PERMISSION_CACHE_TTL = 600000; // 10 minutes

interface PermissionCacheEntry {
  email: string;
  data: UserPermissions;
  timestamp: number;
}

/**
 * Load permissions from localStorage cache
 */
function loadPermissionsFromCache(email: string): UserPermissions | null {
  try {
    const cached = localStorage.getItem(PERMISSION_CACHE_KEY);
    if (!cached) return null;

    const entry: PermissionCacheEntry = JSON.parse(cached);
    const now = Date.now();
    const age = now - entry.timestamp;

    if (entry.email === email && age < PERMISSION_CACHE_TTL) {
      return entry.data;
    }

    return null;
  } catch (error) {
    return null;
  }
}

interface UseAllPageAccessResult {
  accessiblePages: Set<string>;
  isLoading: boolean;
}

/**
 * Returns a set of page IDs that the current user has access to
 * @param userEmail - Email address of the user to check permissions for
 */
export function useAllPageAccess(userEmail?: string | null): UseAllPageAccessResult {
  const currentUser = useFirebaseStore((state) => state.currentUser);

  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user permissions with caching (same strategy as usePagePermissions)
  // 1. Try localStorage cache first (fastest - cross-app navigation)
  // 2. Fall back to Zustand store (already loaded by AuthWrapper/AuthGuard)
  // 3. If neither available, permissions are null (no access)
  useEffect(() => {
    if (!userEmail) {
      setIsLoading(false);
      setUserPermissions(null);
      return;
    }

    // Try localStorage cache first
    const cachedPermissions = loadPermissionsFromCache(userEmail);
    if (cachedPermissions) {
      setUserPermissions(cachedPermissions);
      setIsLoading(false);
      return;
    }

    // Cache miss - use Zustand store if available
    if (currentUser && currentUser.email === userEmail) {
      const permissions: UserPermissions = {
        email: currentUser.email,
        role: currentUser.role,
        pageAccess: (currentUser.pagePermissions || {}) as PageAccessConfig,
        defaultLocation: currentUser.defaultLocation,
      };
      setUserPermissions(permissions);
      setIsLoading(false);
      return;
    }

    // No data available yet - still loading
    setIsLoading(false);
    setUserPermissions(null);
  }, [userEmail, currentUser]);

  // Compute accessible pages
  const accessiblePages = useMemo((): Set<string> => {
    const accessible = new Set<string>();

    // Add all public pages
    PAGES.forEach((page) => {
      if (!page.requiresAuth) {
        accessible.add(page.id);
      }
    });

    // If no user permissions loaded yet, only public pages are accessible
    if (!userPermissions) {
      return accessible;
    }

    // Admins have access to all pages
    const isAdmin = userPermissions.role === 'admin';
    if (isAdmin) {
      PAGES.forEach((page) => {
        accessible.add(page.id);
      });
      return accessible;
    }

    // Regular user - check each authenticated page
    PAGES.forEach((page) => {
      if (!page.requiresAuth) {
        return; // Already added public pages above
      }

      // Manager-flag holders (e.g. quiz managers) open the page at every location
      if (holdsManagerFlag(page, currentUser)) {
        accessible.add(page.id);
        return;
      }

      const pageAccess = userPermissions.pageAccess?.[page.id];

      // No access if page not in pageAccess or not enabled
      if (!pageAccess || !pageAccess.enabled) {
        return;
      }

      // For location-based pages, user must have at least one location (an Editor one on
      // editorsOnly pages). A grant without a locations map (e.g. from before the page
      // became per store) opens nothing, matching usePagePermissions.
      if (page.hasLocations) {
        if (isLocationBasedAccess(pageAccess) && grantedLocationIds(page, pageAccess).length > 0) {
          accessible.add(page.id);
        }
        return;
      }

      // Simple pages and role-based pages just need enabled: true
      accessible.add(page.id);
    });

    return accessible;
  }, [userPermissions, currentUser]);

  return {
    accessiblePages,
    isLoading,
  };
}
